// Portal Pages Service
// Handles API calls for portal page management
import api from './api';

const pagesService = {
  // ============================================================================
  // PAGES MANAGEMENT
  // ============================================================================

  // Get all pages for the authenticated user
  getPages: async () => {
    try {
      console.log('PagesService - Fetching pages');
      const response = await api.get('/pages');
      console.log('PagesService - Pages fetched:', response);
      return response;
    } catch (error) {
      console.error('PagesService - Error fetching pages:', error);
      throw new Error(error.message || 'Failed to fetch pages');
    }
  },

  // Get a specific page with content blocks
  getPage: async (pageId) => {
    try {
      console.log('PagesService - Fetching page:', pageId);
      const response = await api.get(`/pages/${pageId}`);
      console.log('PagesService - Page fetched:', response);
      return response;
    } catch (error) {
      console.error('PagesService - Error fetching page:', error);
      throw new Error(error.message || 'Failed to fetch page');
    }
  },

  // Create a new page
  createPage: async (pageData) => {
    try {
      console.log('PagesService - Creating page:', pageData);
      const response = await api.post('/pages', pageData);
      console.log('PagesService - Page created:', response);
      return response;
    } catch (error) {
      console.error('PagesService - Error creating page:', error);
      throw new Error(error.message || 'Failed to create page');
    }
  },

  // Update a page
  updatePage: async (pageId, pageData) => {
    try {
      console.log('PagesService - Updating page:', pageId, pageData);
      const response = await api.put(`/pages/${pageId}`, pageData);
      console.log('PagesService - Page updated:', response);
      return response;
    } catch (error) {
      console.error('PagesService - Error updating page:', error);
      throw new Error(error.message || 'Failed to update page');
    }
  },

  // Delete a page
  deletePage: async (pageId) => {
    try {
      console.log('PagesService - Deleting page:', pageId);
      const response = await api.delete(`/pages/${pageId}`);
      console.log('PagesService - Page deleted:', response);
      return response;
    } catch (error) {
      console.error('PagesService - Error deleting page:', error);
      throw new Error(error.message || 'Failed to delete page');
    }
  },

  // ============================================================================
  // CONTENT BLOCKS MANAGEMENT
  // ============================================================================

  // Add a content block to a page
  createBlock: async (pageId, blockData) => {
    try {
      console.log('PagesService - Creating block for page:', pageId, blockData);
      const response = await api.post(`/pages/${pageId}/blocks`, blockData);
      console.log('PagesService - Block created:', response);
      return response;
    } catch (error) {
      console.error('PagesService - Error creating block:', error);
      throw new Error(error.message || 'Failed to create block');
    }
  },

  // Update a content block
  updateBlock: async (pageId, blockId, blockData) => {
    try {
      console.log('PagesService - Updating block:', pageId, blockId, blockData);
      const response = await api.put(`/pages/${pageId}/blocks/${blockId}`, blockData);
      console.log('PagesService - Block updated:', response);
      return response;
    } catch (error) {
      console.error('PagesService - Error updating block:', error);
      throw new Error(error.message || 'Failed to update block');
    }
  },

  // Delete a content block
  deleteBlock: async (pageId, blockId) => {
    try {
      console.log('PagesService - Deleting block:', pageId, blockId);
      const response = await api.delete(`/pages/${pageId}/blocks/${blockId}`);
      console.log('PagesService - Block deleted:', response);
      return response;
    } catch (error) {
      console.error('PagesService - Error deleting block:', error);
      throw new Error(error.message || 'Failed to delete block');
    }
  },

  // Reorder content blocks
  reorderBlocks: async (pageId, blockOrders) => {
    try {
      console.log('PagesService - Reordering blocks:', pageId, blockOrders);
      const response = await api.put(`/pages/${pageId}/blocks/reorder`, { blockOrders });
      console.log('PagesService - Blocks reordered:', response);
      return response;
    } catch (error) {
      console.error('PagesService - Error reordering blocks:', error);
      throw new Error(error.message || 'Failed to reorder blocks');
    }
  },

  // ============================================================================
  // DRAFT/PUBLISH WORKFLOW
  // ============================================================================

  // Save content blocks as draft
  saveDraft: async (pageId, contentBlocks) => {
    try {
      console.log('PagesService - Saving draft:', pageId, contentBlocks);
      const response = await api.put(`/pages/${pageId}/draft`, { contentBlocks });
      console.log('PagesService - Draft saved:', response);
      return response;
    } catch (error) {
      console.error('PagesService - Error saving draft:', error);
      throw new Error(error.message || 'Failed to save draft');
    }
  },

  // Publish draft content to live site
  publishPage: async (pageId) => {
    try {
      console.log('PagesService - Publishing page:', pageId);
      const response = await api.post(`/pages/${pageId}/publish`);
      console.log('PagesService - Page published:', response);
      return response;
    } catch (error) {
      console.error('PagesService - Error publishing page:', error);
      throw new Error(error.message || 'Failed to publish page');
    }
  },

  // ============================================================================
  // PUBLIC PAGES (for viewing published pages)
  // ============================================================================

  // Get all published pages for a user
  getPublicPages: async (userId) => {
    try {
      console.log('PagesService - Fetching public pages for user:', userId);
      const response = await api.get(`/pages/public/${userId}`);
      console.log('PagesService - Public pages fetched:', response);
      return response;
    } catch (error) {
      console.error('PagesService - Error fetching public pages:', error);
      throw new Error(error.message || 'Failed to fetch public pages');
    }
  },

  // Get a published page by slug with content blocks
  getPublicPage: async (userId, slug) => {
    try {
      console.log('PagesService - Fetching public page:', userId, slug);
      const response = await api.get(`/pages/public/${userId}/${slug}`);
      console.log('PagesService - Public page fetched:', response);
      return response;
    } catch (error) {
      console.error('PagesService - Error fetching public page:', error);
      throw new Error(error.message || 'Failed to fetch public page');
    }
  },
};

export default pagesService;
